import React, { useEffect, useRef, useState } from 'react';

import { FormattedMessage } from 'react-intl-next';

import Button, { type ButtonProps } from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import AddIcon from '@atlaskit/icon/core/add';
import { Box, Flex, Text } from '@atlaskit/primitives/compiled';
import Spinner from '@atlaskit/spinner';
import { token } from '@atlaskit/tokens';
import type { User as UserPickerUser } from '@atlaskit/user-picker';

import { type TeamUser, useTeamCollabQuery } from '../../clients/use-collab-client';
import { parseUserAriToUserId } from '../../clients/utils';
import { messages } from '../i18n';

const ENTRY_DURATION = 0.36;
const EXIT_DURATION = 0.12;
const MAX_SUGGESTED_USERS = 2;

const styles = cssMap({
	container: {
		marginTop: token('space.025'),
		width: '100%',
		position: 'relative',
	},
	entry: {
		flex: '0 1 auto',
		maxWidth: '50%',
		opacity: 0,
		paddingTop: token('space.050'),
		paddingRight: token('space.050'),
		paddingBottom: token('space.050'),
		paddingLeft: token('space.050'),
		transition: `opacity ${ENTRY_DURATION}s ease-in`,
	},
	base: {
		flex: '0 1 auto',
		maxWidth: '50%',
		opacity: 1,
		paddingTop: token('space.050'),
		paddingRight: token('space.050'),
		paddingBottom: token('space.050'),
		paddingLeft: token('space.050'),
		overflow: 'hidden',
		transition: `opacity ${ENTRY_DURATION}s ease-in`,
	},
	exit: {
		flex: '0 1 auto',
		maxWidth: '0%',
		opacity: 0,
		overflow: 'hidden',
		paddingTop: token('space.0'),
		paddingRight: token('space.0'),
		paddingBottom: token('space.0'),
		paddingLeft: token('space.0'),
		transition: `all ${EXIT_DURATION}s ease-out`,
	},
	recommendedUsersTitleContainer: {
		color: token('color.text.subtle'),
		marginBlockStart: token('space.100'),
		marginBlockEnd: token('space.050'),
	},
});

interface SuggestedMemberPillProps {
	user: UserPickerUser;
	onClick: (user: UserPickerUser) => void;
}

const SuggestedMemberPill = ({ user, onClick }: SuggestedMemberPillProps) => {
	const [animationState, setAnimationState] = useState('entry');

	const handleUserPillClick: ButtonProps['onClick'] = () => {
		setAnimationState('exit');
		setTimeout(() => {
			onClick(user);
		}, EXIT_DURATION * 1000);
	};

	useEffect(() => {
		setTimeout(() => setAnimationState('base'));
	}, []);

	return (
		<Box
			xcss={
				animationState === 'entry'
					? styles.entry
					: animationState === 'exit'
						? styles.exit
						: styles.base
			}
		>
			<Button
				spacing="compact"
				appearance="default"
				iconBefore={AddIcon}
				onClick={handleUserPillClick}
			>
				{user.name}
			</Button>
		</Box>
	);
};

interface SuggestedTeamMembersProps {
	currentPickedUsers: UserPickerUser[];
	onSuggestedUserClick: (selectedUser: UserPickerUser) => void;
	initialValues?: UserPickerUser[];
	cloudId?: string;
	accountId?: string;
}

// Transform TeamUser to UserPickerUser
const transformTeamUserToUserPickerUser = (user: TeamUser): UserPickerUser => ({
	id: parseUserAriToUserId(user.id),
	name: user.name,
	publicName: user.name,
	avatarUrl: user.picture,
	type: 'user',
	email: '',
});

export const SuggestedTeamMembers = ({
	onSuggestedUserClick,
	currentPickedUsers,
	initialValues = [],
	cloudId = '',
	accountId = '',
}: SuggestedTeamMembersProps) => {
	const { data: teamUsers, loading: isLoading, refetch } = useTeamCollabQuery(accountId, cloudId);
	const previousSuggestionCountRef = useRef<number>(0);
	const hasTriggeredRefetchRef = useRef(false);

	const suggestedUsers = teamUsers.map(transformTeamUserToUserPickerUser);
	

	const filteredSuggestedUsers = suggestedUsers
		.filter(
			(user) =>
				!currentPickedUsers.some((pickedUser) => pickedUser.id === user.id) &&
				!initialValues.some((initialUser) => initialUser.id === user.id),
		)
		.slice(0, MAX_SUGGESTED_USERS);

	useEffect(() => {
		const currentCount = filteredSuggestedUsers.length;
		const previousCount = previousSuggestionCountRef.current;

		if (previousCount > 1 && currentCount === 1 && !hasTriggeredRefetchRef.current && !isLoading) {
			hasTriggeredRefetchRef.current = true;
			refetch()
				.then(() => {
					hasTriggeredRefetchRef.current = false;
				})
				.catch(() => {
					hasTriggeredRefetchRef.current = false;
				});
		}

		if (currentCount !== previousCount) {
			previousSuggestionCountRef.current = currentCount;
		}
	}, [filteredSuggestedUsers.length, refetch, isLoading]);

	if (isLoading) {
		return (
			<Flex justifyContent="center" alignItems="center" xcss={styles.container}>
				<Spinner size="small" />
			</Flex>
		);
	}

	if (!filteredSuggestedUsers.length) {
		return null;
	}

	return (
		<>
			<Box xcss={styles.recommendedUsersTitleContainer}>
				<Text weight="bold" size="small">
					<FormattedMessage {...messages.recommendedUsersTitle} />
				</Text>
			</Box>
			<Flex
				wrap="nowrap"
				justifyContent="start"
				alignItems="center"
				direction="row"
				xcss={styles.container}
			>
				{filteredSuggestedUsers.map((user) => (
					<SuggestedMemberPill user={user} onClick={onSuggestedUserClick} key={user.id} />
				))}
			</Flex>
		</>
	);
};
